import * as React from 'react';

import { type ImgParams, getOptimizedSrcs } from 'js/lib/imgix';

import type { ResponsiveImageType } from 'bundles/cds-labs/components/Modules/HeroBentoModule/ResponsiveImage.types';

export type FetchPriority = React.JSX.IntrinsicElements['img']['fetchPriority'];

export const FETCH_PRIORITIES: Record<NonNullable<FetchPriority>, NonNullable<FetchPriority>> = {
  high: 'high',
  auto: 'auto',
  low: 'low',
} as const;

/**
 * A single image's computed, Imgix-optimized sources for one viewport (media query).
 */
export type ResponsiveImageSource = {
  /**
   * The original image descriptor this source was computed from. Retains `media`,
   * `alt`, `isFallback`, etc. so consumers can build whatever markup they need.
   */
  image: ResponsiveImageType;
  /**
   * The 1x (dpr=1) URL, suitable for the `src` attribute or a CSS `url()` fallback.
   */
  src: string;
  /**
   * The full DPR srcset string (e.g. `"<dpr1> 1x, <dpr2> 2x, <dpr3> 3x"`), suitable
   * for `<img srcset>`, `<source srcset>`, `<link imageSrcSet>`, or CSS `image-set()`.
   */
  srcSet: string;
};

/**
 * A `rel="preload"` link descriptor, shaped for react-helmet's `link` prop. Used to
 * preload the responsive image(s) so high-priority hero imagery loads sooner.
 */
export type ResponsiveImagePreloadLink = React.JSX.IntrinsicElements['link'];

export type UseResponsiveImageSourcesResult = {
  /**
   * Per-image computed sources, in input order. Images without a usable URL are
   * filtered out, so this can be shorter than the input array.
   */
  sources: ResponsiveImageSource[];
  /**
   * The source flagged `isFallback` (the one used as the `<img>` / background fallback),
   * or `null` if none of the resolvable images were marked as the fallback.
   */
  fallback: ResponsiveImageSource | null;
  /**
   * `rel="preload"` link descriptors for each source, for use with react-helmet.
   * Build these into a `<Helmet link={...} />` only when preloading is desired
   * (typically when `fetchPriority === 'high'`).
   */
  preloadLinks: ResponsiveImagePreloadLink[];
};

const DEFAULT_IMG_PARAMS = {
  quality: 40,
  fit: 'clip',
  auto: 'format, compress',
};

const getImageSrcData = (image: ResponsiveImageType, imgParams?: ImgParams): { src: string; srcSet: string } | null => {
  const imgParamsToUse = { ...DEFAULT_IMG_PARAMS, ...imgParams };

  if (!image.url) {
    return null;
  }

  const width = image.width ?? undefined;
  const height = image.height ?? undefined;

  const srcs = getOptimizedSrcs(image.url, width, height, imgParamsToUse);

  if (!srcs.dpr1) {
    return null;
  }

  const srcSet = srcs.dpr2 && srcs.dpr3 ? `${srcs.dpr1} 1x, ${srcs.dpr2} 2x, ${srcs.dpr3} 3x` : `${srcs.dpr1} 1x`;

  return {
    src: srcs.dpr1,
    srcSet,
  };
};

/**
 * Computes Imgix-optimized, DPR-aware sources and preload links for a set of
 * responsive images. This is the shared engine behind both `<ResponsiveImage>`
 * (which renders a `<picture>`) and background-image consumers (which can feed the
 * `srcSet`/`src` into CSS `image-set()` and reuse the preload links).
 *
 * Returns raw data only — no JSX — so the caller decides how to render it.
 *
 * @param images - The responsive image descriptors (one per viewport/media query).
 * @param fetchPriority - The fetch priority applied to the generated preload links.
 * @param imgParams - Optional Imgix params merged over the defaults (quality, fit, etc.).
 */
const useResponsiveImageSources = (
  images: ResponsiveImageType[],
  fetchPriority: FetchPriority,
  imgParams?: ImgParams
): UseResponsiveImageSourcesResult => {
  return React.useMemo(() => {
    const sources: ResponsiveImageSource[] = [];
    let fallback: ResponsiveImageSource | null = null;
    const preloadLinks: ResponsiveImagePreloadLink[] = [];

    images.forEach((image) => {
      const srcData = getImageSrcData(image, imgParams);
      if (!srcData) {
        return;
      }

      const source: ResponsiveImageSource = { image, src: srcData.src, srcSet: srcData.srcSet };
      sources.push(source);

      if (image.isFallback) {
        fallback = source;
      }

      preloadLinks.push({
        rel: 'preload',
        as: 'image',
        href: srcData.src,
        imageSrcSet: srcData.srcSet,
        fetchPriority,
        media: image.media,
      });
    });

    return { sources, fallback, preloadLinks };
  }, [images, fetchPriority, imgParams]);
};

export default useResponsiveImageSources;
